import React, { useState, useEffect } from 'react';
import Toast from './Toast';
import { generateCheckoutUrl } from '../config/lemonsqueezy';
import { supabase } from '../services/supabase';

/*
웹에서 결제 프로세스:
  1. 사용자가 요금제 선택 → "구독하기" 버튼 클릭
  2. 로그인 확인 (로그인 안 되어있으면 로그인 페이지로)
  3. LemonSqueezy Checkout 페이지로 리다이렉트
  4. 결제 완료 후 /payment-success 페이지로 리턴
  5. 5초 후 자동으로 대시보드로 이동

환경별 자동 전환:
  - 개발: dev.aivuebiz.com 또는 localhost → Test Mode Variant IDs 사용
  - 운영: aivuebiz.com → Production Mode Variant IDs 사용
*/

interface Plan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  popular?: boolean;
  planId?: 'personal' | 'business' | 'enterprise'; // LemonSqueezy plan ID
}

interface PricingCardsProps {
  plans: Plan[];
  language: string;
}

const PricingCards: React.FC<PricingCardsProps> = ({ plans, language }) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // 현재 로그인된 사용자 확인
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const handleSubscribe = async (plan: Plan) => {
    // Free 플랜은 결제 불필요
    if (plan.name.toLowerCase().includes('free') || plan.name.includes('무료')) {
      setToastMessage(language === 'ko' ? '무료 플랜입니다' : 'This is a free plan');
      setShowToast(true);
      return;
    }

    // 로그인 확인
    if (!currentUser) {
      setToastMessage(language === 'ko' 
        ? '구독하려면 먼저 로그인하세요' 
        : 'Please login to subscribe');
      setShowToast(true);
      
      // 로그인 페이지로 리다이렉트 (3초 후)
      setTimeout(() => {
        window.location.href = '/login?redirect=/pricing';
      }, 2000);
      return;
    }

    // planId 매핑 (plan 이름 기반)
    let planId: 'personal' | 'business' | 'enterprise';
    if (plan.name.toLowerCase().includes('personal') || plan.name.includes('개인')) {
      planId = 'personal';
    } else if (plan.name.toLowerCase().includes('business') || plan.name.includes('비즈니스')) {
      planId = 'business';
    } else if (plan.name.toLowerCase().includes('enterprise') || plan.name.includes('엔터프라이즈')) {
      planId = 'enterprise';
    } else {
      setToastMessage(language === 'ko' ? '유효하지 않은 플랜입니다' : 'Invalid plan');
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    try {
      // LemonSqueezy Checkout URL 생성
      const checkoutUrl = generateCheckoutUrl(
        planId,
        currentUser.email,
        currentUser.id
      );

      // Checkout 페이지로 리다이렉트
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      setToastMessage(language === 'ko' 
        ? '결제 페이지 연결에 실패했습니다' 
        : 'Failed to connect to payment page');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };
  // Feature icons mapping
  const getFeatureIcon = (feature: string) => {
    // Message features
    if (feature.includes('message') || feature.includes('메시지') || feature.includes('chat') || feature.includes('채팅')) {
      return '💬';
    }
    // Video features
    if (feature.includes('video') || feature.includes('영상') || feature.includes('call') || feature.includes('통화')) {
      return '📹';
    }
    // Storage features
    if (feature.includes('storage') || feature.includes('저장') || feature.includes('MB') || feature.includes('GB')) {
      return '💾';
    }
    // Retention features
    if (feature.includes('retention') || feature.includes('보존') || feature.includes('month')) {
      return '📅';
    }
    // STT features
    if (feature.includes('STT') || feature.includes('character') || feature.includes('자')) {
      return '🎙️';
    }
    // Group features
    if (feature.includes('group') || feature.includes('그룹') || feature.includes('people') || feature.includes('명')) {
      return '👥';
    }
    // Download features
    if (feature.includes('download') || feature.includes('다운로드') || feature.includes('save') || feature.includes('저장')) {
      return '⬇️';
    }
    // Recording features
    if (feature.includes('recording') || feature.includes('녹화') || feature.includes('record')) {
      return '🔴';
    }
    // Screen share features
    if (feature.includes('screen') || feature.includes('화면') || feature.includes('share') || feature.includes('공유')) {
      return '🖥️';
    }
    // Conference features
    if (feature.includes('conference') || feature.includes('회의') || feature.includes('12')) {
      return '🎯';
    }
    // Bulk features
    if (feature.includes('bulk') || feature.includes('일괄') || feature.includes('batch')) {
      return '📦';
    }
    // Custom features
    if (feature.includes('custom') || feature.includes('맞춤')) {
      return '⚙️';
    }
    // Unlimited features
    if (feature.includes('unlimited') || feature.includes('무제한')) {
      return '∞';
    }
    // Default
    return '✅';
  };

  const getPlanColor = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('free') || name.includes('무료')) return 'gray';
    if (name.includes('personal') || name.includes('개인')) return 'blue';
    if (name.includes('business') || name.includes('비즈니스')) return 'purple';
    if (name.includes('enterprise') || name.includes('엔터프라이즈')) return 'indigo';
    return 'primary';
  };

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
      {plans.map((plan, index) => {
        const color = getPlanColor(plan.name);
        
        return (
          <div 
            key={index}
            className={`relative group ${plan.popular ? 'lg:scale-105 z-10' : ''}`}
          >
            {/* Popular badge */}
            {plan.popular && (
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-20">
                <span className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-bold rounded-full shadow-lg">
                  {language === 'ko' ? '인기' : 'POPULAR'}
                </span>
              </div>
            )}
            
            {/* Card */}
            <div className={`relative p-6 h-full flex flex-col ${
              plan.popular 
                ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white' 
                : 'bg-white'
            } rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${
              plan.popular ? '' : 'border border-gray-200'
            }`}>
              {/* Plan icon */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                plan.popular 
                  ? 'bg-white/20' 
                  : `bg-${color}-100`
              }`}>
                <span className="text-2xl">
                  {plan.name.includes('Free') || plan.name.includes('무료') ? '🎁' :
                   plan.name.includes('Personal') || plan.name.includes('개인') ? '👤' :
                   plan.name.includes('Business') || plan.name.includes('비즈니스') ? '💼' :
                   '🏢'}
                </span>
              </div>
              
              {/* Header */}
              <div className="mb-4">
                <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm ${plan.popular ? 'text-white/90' : 'text-gray-600'}`}>
                  {plan.description}
                </p>
              </div>
              
              {/* Price */}
              <div className="mb-6">
                <span className={`text-3xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.price}
                </span>
                <span className={`ml-1 text-sm ${plan.popular ? 'text-white/80' : 'text-gray-500'}`}>
                  {plan.period}
                </span>
              </div>
              
              {/* Features */}
              <div className="space-y-2.5 mb-6 flex-grow">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start">
                    <span className="mr-2 mt-0.5 text-sm">
                      {getFeatureIcon(feature)}
                    </span>
                    <span className={`text-sm leading-relaxed ${plan.popular ? 'text-white/90' : 'text-gray-700'}`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* CTA Button 나중에 웹에서도 결제 처리하려면 주석을 풀 것 !!! */}
              {/* <button 
                onClick={() => handleSubscribe(plan)}
                disabled={isLoading}
                className={`block w-full py-3 px-4 text-center font-semibold rounded-xl transition-all duration-300 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                  plan.popular 
                    ? 'bg-white text-purple-700 hover:bg-gray-50 shadow-lg' 
                    : plan.name.toLowerCase().includes('free') || plan.name.includes('무료')
                      ? 'bg-gray-600 text-white hover:bg-gray-700 hover:shadow-lg'
                      : plan.name.toLowerCase().includes('personal') || plan.name.includes('개인')
                      ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                      : plan.name.toLowerCase().includes('enterprise') || plan.name.includes('엔터프라이즈')
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-lg'
                      : 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg'
                }`}
              >
                {isLoading 
                  ? (language === 'ko' ? '처리중...' : 'Processing...') 
                  : plan.name.toLowerCase().includes('free') || plan.name.includes('무료')
                    ? (language === 'ko' ? '시작하기' : 'Get Started')
                    : (language === 'ko' ? '구독하기' : 'Subscribe')}
              </button> */}
            </div>
          </div>
        );
      })}
      </div>
      <Toast 
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </>
  );
};

export default PricingCards;